import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useState, ReactNode, SyntheticEvent, useRef, useEffect, forwardRef, useCallback } from 'react';
import { InView } from 'react-intersection-observer';
import { tabsContentStyles, getTabsListWrapStyles, getTabsWrapperStyles, tabsStyles, getTabStyles, getTabPanelStyles } from './TAGTabs.styles';
import { getMinHeaderHeightStyles } from '../Header/Header.styles';
import { DESKTOP_HEADER_ID, DESKTOP_HEADER_ID_WITH_NAV_MENU, MOBILE_HEADER_ID } from '@shared-components/constants';
import { useAppContext } from '@shared-components/context';
import { TTabsSize, TTabRowVariant, Maybe } from '@shared-components/types';
interface TabPanelProps {
  children?: ReactNode;
  value: string;
  currentValue: string | null;
  role: 'default' | 'navigation';
  withContentInnerGap?: boolean;
  isSmallScreen?: boolean;
}
const CustomTabPanel = forwardRef<HTMLDivElement, TabPanelProps>(({
  children,
  value,
  currentValue,
  role,
  withContentInnerGap,
  isSmallScreen
}, ref) => {
  return <div role="tabpanel" hidden={role === 'default' && currentValue !== value} aria-labelledby={`tab-${value}`} id={`tabpanel-${value}`} ref={ref} style={getTabPanelStyles({
    withContentInnerGap,
    isSmallScreen
  })}>
				{children}
			</div>;
});
CustomTabPanel.displayName = 'CustomTabPanel';
const a11yProps = (ariaControlsText: string) => {
  return {
    id: `tab-${ariaControlsText}`,
    'aria-controls': ariaControlsText
  };
};
interface TAGTabsProps {
  tabs: {
    label: string;
    value: string;
    href?: string;
    jumplinkName?: string;
    content: ReactNode;
  }[];
  role?: 'default' | 'navigation';
  centered?: boolean;
  dataTestId?: string;
  tabRowVariant?: TTabRowVariant;
  tabsSize?: TTabsSize;
  withContentInnerGap?: boolean;
  jumplinkTabs?: Maybe<boolean>;
}
export default function TAGTabs({
  tabs,
  role = 'default',
  centered,
  dataTestId = '',
  tabRowVariant = 'Standard',
  tabsSize = 'Small',
  withContentInnerGap = false,
  jumplinkTabs = false
}: Readonly<TAGTabsProps>) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isBelowMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [currentValue, setCurrentValue] = useState<null | string>(jumplinkTabs ? null : tabs[0].value);
  const [isTabsFixed, setIsTabsFixed] = useState(false);
  const [forceRender, setForceRender] = useState(false);
  const tabsWrapNode = useRef<HTMLDialogElement>(null);
  const {
    config
  } = useAppContext();
  const showMobileHeaderLocationBar = config?.featureFlags?.showMobileHeaderLocationBar;
  const showHeaderWithLocationNav = config.featureFlags?.showHeaderWithLocationNav;
  const showHeaderNavigationMenu = config.featureFlags?.showHeaderNavigationMenu;
  const withDisappearingHeader = config.features?.header?.hideHeaderOnScroll;
  const desktopHeaderElement = globalThis?.document?.getElementById(withDisappearingHeader ? DESKTOP_HEADER_ID_WITH_NAV_MENU : DESKTOP_HEADER_ID);
  const mobileHeaderElement = globalThis?.document?.getElementById(MOBILE_HEADER_ID);
  const desktopHeaderDisappearingTopStyle = desktopHeaderElement?.style?.top;
  const mobileHeaderDisappearingTopStyle = mobileHeaderElement?.style?.top;
  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setCurrentValue(newValue);
  };
  const handleJumplink = (jumplinkName: string) => {
    if (jumplinkName && globalThis.window) {
      globalThis.window.location.hash = jumplinkName;
    }
  };
  const handleScroll = useCallback(() => {
    if (tabsWrapNode.current) {
      const blockOffsetTop = tabsWrapNode.current.offsetTop;
      if (globalThis.window.scrollY >= blockOffsetTop) {
        setIsTabsFixed(true);
        setForceRender(prev => !prev);
      } else {
        setIsTabsFixed(false);
      }
    }
  }, []);
  const setInView = (inView: boolean, entry: IntersectionObserverEntry) => {
    if (role !== 'navigation') return;
    if (inView) {
      setCurrentValue(entry.target.getAttribute('id'));
    }
  };
  useEffect(() => {
    if (role !== 'navigation') return;
    globalThis.window.addEventListener('scroll', handleScroll);
    return () => {
      globalThis.window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, role]);
  const headerPositioning = getMinHeaderHeightStyles(showMobileHeaderLocationBar, showHeaderNavigationMenu, showHeaderWithLocationNav);
  return <Box sx={tabsContentStyles} ref={tabsWrapNode} data-testid={'tag_tabs'} data-force-render={forceRender} data-sentry-element="Box" data-sentry-component="TAGTabs" data-sentry-source-file="TAGTabs.tsx">
			<Box sx={getTabsListWrapStyles({
      tabRowVariant,
      isSmallScreen
    })} data-sentry-element="Box" data-sentry-source-file="TAGTabs.tsx">
				<Box sx={getTabsWrapperStyles({
        tabRowVariant,
        isTabsFixed,
        headerPositioning,
        withDisappearingHeader,
        desktopHeaderDisappearingTopStyle,
        mobileHeaderDisappearingTopStyle
      })} data-sentry-element="Box" data-sentry-source-file="TAGTabs.tsx">
					<Tabs value={currentValue || false} onChange={handleChange} centered={centered} variant={isBelowMediumScreen ? 'scrollable' : 'fullWidth'} sx={tabsStyles} scrollButtons={false} data-sentry-element="Tabs" data-sentry-source-file="TAGTabs.tsx">
						{tabs.map(({
            label,
            href,
            value,
            jumplinkName
          }) => <Tab key={value} sx={getTabStyles({
            tabsSize,
            tabRowVariant,
            isSmallScreen
          })} {...a11yProps(jumplinkName ?? value)} {...href ? {
            href
          } : {}} value={value} label={label} data-testid={`${dataTestId}_${value}`} onClick={() => jumplinkName && handleJumplink(jumplinkName)} />)}
					</Tabs>
				</Box>
			</Box>
			{!jumplinkTabs && tabs.map(({
      content,
      value
    }) => <InView onChange={setInView} threshold={0.5} key={value} id={value}>
						<CustomTabPanel value={value} currentValue={currentValue} role={role} withContentInnerGap={withContentInnerGap} isSmallScreen={isSmallScreen}>
							{content}
						</CustomTabPanel>
					</InView>)}
		</Box>;
}